/**
 * My attendance — /learn/attendance (the student) and /attendance/student (a parent)
 *
 * Read-only: the headline percentage, a split per batch, and the day-by-day record.
 * Nobody but a teacher or an admin can change a mark, so there is nothing to edit here.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import RecordPage, { FormSection } from '../../components/shell/RecordPage';
import { StatTiles } from '../../components/shell/Page';
import { useTabLabel } from '../../components/shell/TabsContext';
import { STATUS_META, rateTone } from './Attendance';
import { toast } from 'sonner';
import { CalendarCheck, ChartBar, CheckCircle, XCircle, Users } from '@phosphor-icons/react';

const prettyDate = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso
    : d.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
};

export default function MyAttendance() {
  const { api, user } = useAuth();
  const [params] = useSearchParams();
  const isParent = user?.role === 'parent';
  const [children, setChildren] = useState([]);
  const [studentId, setStudentId] = useState(params.get('student') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useTabLabel(isParent ? 'Attendance' : 'My attendance');

  useEffect(() => {
    if (!isParent) return;
    api.get('/diary/students')
      .then((r) => {
        setChildren(r.data || []);
        setStudentId((cur) => cur || r.data?.[0]?.id || '');
      })
      .catch(() => setChildren([]));
  }, [api, isParent]);

  const load = useCallback(async () => {
    if (isParent && !studentId) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api.get(`/attendance/student${studentId ? `?student_id=${studentId}` : ''}`);
      setData(r.data);
    } catch (e) {
      setData(null);
      toast.error(e.response?.data?.detail || 'Could not load attendance');
    } finally {
      setLoading(false);
    }
  }, [api, studentId, isParent]);
  useEffect(() => { load(); }, [load]);

  const records = data?.records || [];
  const counts = data?.counts || {};

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="my-attendance-page"
        title={isParent ? `Attendance — ${data?.student?.name || ''}` : 'My attendance'}
        subtitle={data?.student?.member_id ? `ID ${data.student.member_id}` : 'Your record, class by class'}
        onBack={null}
        onRefresh={load}
        refreshing={loading}
        loading={loading && !data}
      >
        {isParent && (
          <FormSection title="Child" icon={Users} bodyClassName="p-3">
            <select className="form-select w-[240px]" value={studentId}
              onChange={(e) => setStudentId(e.target.value)} data-testid="attendance-child">
              {children.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </FormSection>
        )}

        <StatTiles items={[
          { label: 'Attendance', value: data?.rate === null || data?.rate === undefined ? '—' : `${data.rate}%`, icon: ChartBar, iconClass: 'bg-sky-100 text-sky-700', testid: 'my-rate' },
          { label: 'Classes attended', value: (counts.present || 0) + (counts.late || 0), icon: CheckCircle, iconClass: 'bg-emerald-100 text-emerald-700', testid: 'my-present' },
          { label: 'Missed', value: counts.absent || 0, icon: XCircle, iconClass: counts.absent ? 'bg-red-100 text-red-700' : undefined, testid: 'my-absent' },
          { label: 'Classes recorded', value: data?.total_sessions || 0, icon: CalendarCheck, testid: 'my-total' },
        ]} />

        {(data?.by_batch || []).length > 1 && (
          <FormSection title="By batch" icon={Users} bodyClassName="p-0">
            <table className="w-full text-[13px]">
              <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Batch</th>
                  <th className="text-right font-medium px-3 py-2">Classes</th>
                  <th className="text-right font-medium px-3 py-2">Attendance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.by_batch.map((b) => (
                  <tr key={b.batch_id} data-testid={`my-batch-${b.batch_id}`}>
                    <td className="px-3 py-1.5">{b.batch_name || '—'}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{b.sessions}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${rateTone(b.rate)}`}>
                      {b.rate === null ? '—' : `${b.rate}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </FormSection>
        )}

        <FormSection title="Day by day" icon={CalendarCheck} bodyClassName="p-0">
          {records.length === 0 ? (
            <p className="p-4 text-[13px] text-muted-foreground" data-testid="no-attendance">
              No attendance has been recorded yet.
            </p>
          ) : (
            <table className="w-full text-[13px]" data-testid="my-attendance-list">
              <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Date</th>
                  <th className="text-left font-medium px-3 py-2">Batch</th>
                  <th className="text-left font-medium px-3 py-2">Mark</th>
                  <th className="text-left font-medium px-3 py-2">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {records.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.present;
                  const Icon = meta.icon;
                  return (
                    <tr key={r.id} data-testid={`attendance-day-${r.date}`}>
                      <td className="px-3 py-1.5 whitespace-nowrap">{prettyDate(r.date)}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">{r.batch_name || '—'}</td>
                      <td className="px-3 py-1.5">
                        <span className={`inline-flex items-center gap-1 rounded px-1.5 h-5 text-[10px] font-semibold uppercase ${meta.on}`}>
                          <Icon className="w-3 h-3" weight="fill" />{meta.label}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-muted-foreground">{r.note || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </FormSection>
      </RecordPage>
    </DashboardLayout>
  );
}
